import moment, { Moment } from 'moment-timezone';
import {
  EmployeeRole,
  FulfillmentName,
  GetAvailableOrderFulfillmentResponse,
} from '@/types';
import {
  findAvailabilityDay,
  OrderAvailability,
} from '@domain/fulfillment-settings/blocs/order-availability';
import { getFulfillmentDisplayName } from '@/experiences/sales-portal/blocs/utils/fulfillment-utils';
import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import {
  Timeslot,
  TimeslotOption,
  TimeZoneMap,
} from '@domain/fulfillment-settings/types/types';
import { useCallback, useMemo } from 'react';

export interface UseFulfillmentSelectionParams {
  additionalOrderValidationRoles: EmployeeRole[];
  availableFulfillments: GetAvailableOrderFulfillmentResponse[];
  employeeRole: EmployeeRole | null;
  savedSelectedFulfillment: SavedSelectedFulfillment | null;
  timezones: TimeZoneMap;
}

export interface TimeslotCategories {
  availableTimeslots: TimeslotOption[];
  disabledTimeslots: TimeslotOption[];
  hasTimeslots: boolean;
}

export interface DateValidationResult {
  isValid: boolean;
  nextValidDate: Moment;
}

export interface UseFulfillmentSelectionReturn {
  /**
   * Creates an isDateDisabled callback for a specific fulfillment type.
   * Used by FulfillmentDropdown to validate dates in the calendar.
   */
  createIsDateDisabled: (
    type: FulfillmentName | null,
  ) => (date: Moment) => boolean;
  // Categorizes timeslots for a date and type into available and disabled.
  getTimeslotCategories: (
    date: string | null,
    type: FulfillmentName | null,
  ) => TimeslotCategories;
  // Display name for the currently saved fulfillment type (for trigger display).
  selectedFulfillmentDisplay: string;
  /**
   * Validates a date for a given fulfillment type.
   * Returns whether valid and the next valid date if not.
   */
  validateFulfillmentDate: (
    date: string,
    type: FulfillmentName,
  ) => DateValidationResult;
  /**
   * Validates a timeslot against available options for a given date and type.
   * Returns the timeslot if valid, null otherwise.
   */
  validateTimeslot: (
    timeslot: Timeslot | null,
    date: Moment,
    type: FulfillmentName,
  ) => Timeslot | null;
}

/**
 * Hook for fulfillment validation and display logic.
 *
 * This hook provides:
 * - Date validation callback for the calendar (based on OrderAvailability)
 * - Timeslot validation and categorization
 * - Display name for the saved fulfillment type
 *
 * Admin bypass: Users with ADMIN or SUPPLIER_DEFAULT roles (or roles in
 * additionalOrderValidationRoles config) can bypass fulfillment restrictions
 * like blackout dates and cutoff times.
 *
 * Note: Pending state (uncommitted changes while dropdown is open) is managed
 * by FulfillmentDropdown component itself, not this hook.
 */
export const useFulfillmentSelection = ({
  additionalOrderValidationRoles,
  availableFulfillments,
  savedSelectedFulfillment,
  employeeRole,
  timezones,
}: UseFulfillmentSelectionParams): UseFulfillmentSelectionReturn => {
  /**
   * Creates an isDateDisabled callback for a specific fulfillment type.
   * This allows the FulfillmentDropdown to validate dates based on the
   * pending type (which may differ from saved type).
   *
   * Admin/override roles can bypass blackout dates and cutoff times.
   */
  const createIsDateDisabled = useCallback(
    (type: FulfillmentName | null) => {
      const typeToValidate = type || availableFulfillments[0]?.fulfillment_name;

      if (!typeToValidate || availableFulfillments.length === 0) {
        return () => true;
      }

      const orderAvailability = new OrderAvailability(
        availableFulfillments,
        timezones,
        typeToValidate,
      );

      return (date: Moment): boolean => {
        return !orderAvailability.isValidSupplierFulfillmentDate(
          date,
          employeeRole,
          additionalOrderValidationRoles,
        );
      };
    },
    [
      additionalOrderValidationRoles,
      availableFulfillments,
      employeeRole,
      timezones,
    ],
  );

  /**
   * Validates a timeslot against available options for a given date and type.
   * Creates its own OrderAvailability for the given type to ensure correct
   * timeslots are checked even when pending type differs from saved type.
   */
  const validateTimeslot = useCallback(
    (
      timeslot: Timeslot | null,
      date: Moment,
      type: FulfillmentName,
    ): Timeslot | null => {
      if (!timeslot || availableFulfillments.length === 0) {
        return null;
      }

      // Create OrderAvailability for the specific type
      const orderAvailability = new OrderAvailability(
        availableFulfillments,
        timezones,
        type,
      );

      const availabilityDay = findAvailabilityDay(
        orderAvailability.selectedAvailability,
        date,
      );
      if (!availabilityDay) {
        return null;
      }

      const options = orderAvailability.getTimeslotOptionsForDay(
        availabilityDay,
        employeeRole,
        additionalOrderValidationRoles,
      );

      const isValid = options.some(
        (ts) =>
          !ts.metadata?.disabled &&
          ts.metadata?.timeslot.startTime === timeslot.startTime &&
          ts.metadata?.timeslot.endTime === timeslot.endTime,
      );
      return isValid ? timeslot : null;
    },
    [
      additionalOrderValidationRoles,
      availableFulfillments,
      employeeRole,
      timezones,
    ],
  );

  /**
   * Validates a date for a given fulfillment type.
   * Returns whether the date is valid and the next valid date if not.
   */
  const validateFulfillmentDate = useCallback(
    (date: string, type: FulfillmentName): DateValidationResult => {
      const orderAvailability = new OrderAvailability(
        availableFulfillments,
        timezones,
        type,
      );

      const dateMoment = moment(date).tz(timezones.restaurant);
      const isValid = orderAvailability.isValidSupplierFulfillmentDate(
        dateMoment,
        employeeRole,
        additionalOrderValidationRoles,
      );

      return {
        isValid,
        nextValidDate: orderAvailability.nextFulfillmentDayMoment,
      };
    },
    [
      additionalOrderValidationRoles,
      availableFulfillments,
      employeeRole,
      timezones,
    ],
  );

  /**
   * Categorizes timeslots for a date and type into available and disabled.
   * Creates its own OrderAvailability for the given type to ensure correct
   * timeslots are returned even when pending type differs from saved type.
   */
  const getTimeslotCategories = useCallback(
    (date: string | null, type: FulfillmentName | null): TimeslotCategories => {
      if (!date || !type || availableFulfillments.length === 0) {
        return {
          availableTimeslots: [],
          disabledTimeslots: [],
          hasTimeslots: false,
        };
      }

      // Create OrderAvailability for the specific type to get correct timeslots
      const orderAvailability = new OrderAvailability(
        availableFulfillments,
        timezones,
        type,
      );

      const dateMoment = moment(date).tz(timezones.restaurant);
      const availabilityDay = findAvailabilityDay(
        orderAvailability.selectedAvailability,
        dateMoment,
      );

      if (!availabilityDay) {
        return {
          availableTimeslots: [],
          disabledTimeslots: [],
          hasTimeslots: false,
        };
      }

      const options = orderAvailability.getTimeslotOptionsForDay(
        availabilityDay,
        employeeRole,
        additionalOrderValidationRoles,
      );

      const available: TimeslotOption[] = [];
      const disabled: TimeslotOption[] = [];

      for (const option of options) {
        if (option.metadata?.disabled) {
          disabled.push(option);
        } else {
          available.push(option);
        }
      }

      return {
        availableTimeslots: available,
        disabledTimeslots: disabled,
        hasTimeslots: options.length > 0,
      };
    },
    [
      additionalOrderValidationRoles,
      availableFulfillments,
      employeeRole,
      timezones,
    ],
  );

  const selectedFulfillmentDisplay = useMemo(
    () =>
      getFulfillmentDisplayName(
        savedSelectedFulfillment?.type ?? null,
        availableFulfillments,
      ),
    [availableFulfillments, savedSelectedFulfillment?.type],
  );

  return {
    createIsDateDisabled,
    getTimeslotCategories,
    selectedFulfillmentDisplay,
    validateFulfillmentDate,
    validateTimeslot,
  };
};
